import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StoreProvider } from './contexts/StoreContext';
import { LoadingProvider } from './contexts/LoadingContext';
import GlobalLoader from './components/common/GlobalLoader';
import LoginPage from './pages/LoginPage';
import StoreSelectionPage from './pages/StoreSelectionPage';
import CreateStorePage from './pages/CreateStorePage';
import StoreOverviewPage from './pages/StoreOverviewPage';
import StoreRequestsPage from './pages/StoreRequestsPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import UserProfilePage from './pages/UserProfilePage';
import ProductsPage from './pages/ProductsPage';
import CategoriesPage from './pages/CategoriesPage';
import SectorsPage from './pages/SectorsPage';
import AdvertisementsPage from './pages/AdvertisementsPage';
import LocationsPage from './pages/LocationsPage';
import DashboardPage from './pages/DashboardPage';
import AllStoresPage from './pages/AllStoresPage';
import AllSectorsPage from './pages/AllSectorsPage';
import AllProductsPage from './pages/AllProductsPage';
import UsersPage from './pages/UsersPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';


const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  if (!isAdmin) {
    return <Navigate to="/select-store" replace />;
  }

  return <>{children}</>;
};

const IndexRedirect: React.FC = () => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  return <Navigate to={isAdmin ? '/dashboard' : '/select-store'} replace />;
};

const FallbackRedirect: React.FC = () => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  return <Navigate to={isAdmin ? '/dashboard' : '/select-store'} replace />;
};

const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  const isManager = currentUser?.role?.role_name?.toLowerCase() === 'manager';
  if (!isAdmin && !isManager) {
    return <Navigate to="/select-store" replace />;
  }

  return <>{children}</>;
};



const AppRoutes: React.FC = () => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <div>Application Loading...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route 
        path="/select-store" 
        element={ 
          <ProtectedRoute>
            <StoreSelectionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/create-store" 
        element={
          <ProtectedRoute>
            <CreateStorePage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/store/:storeId/overview"
        element={
          <ProtectedRoute>
            <StoreOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/store/:storeId/settings"
        element={
          <ManagerRoute>
            <StoreSettingsPage />
          </ManagerRoute>
        }
      />
      <Route 
        path="/store/:storeId/products"
        element={
          <ProtectedRoute>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/store/:storeId/categories"
        element={
          <AdminRoute>
            <CategoriesPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/store/:storeId/sectors"
        element={
          <ProtectedRoute>
            <SectorsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/store-requests"
        element={
          <AdminRoute>
            <StoreRequestsPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/advertisements"
        element={
          <AdminRoute>
            <AdvertisementsPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/locations"
        element={
          <AdminRoute>
            <LocationsPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/dashboard"
        element={
          <AdminRoute>
            <DashboardPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/all-stores"
        element={
          <AdminRoute>
            <AllStoresPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/all-sectors"
        element={
          <AdminRoute>
            <AllSectorsPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/all-products"
        element={
          <AdminRoute>
            <AllProductsPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/users"
        element={
          <AdminRoute>
            <UsersPage />
          </AdminRoute>
        }
      />
      <Route 
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy-policy"
        element={<PrivacyPolicyPage />}
      />
      <Route 
        path="/"
        element={<IndexRedirect />}
      />
      <Route path="*" element={<FallbackRedirect />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <LoadingProvider>
          <StoreProvider>
            <GlobalLoader />
            <AppRoutes />
          </StoreProvider>
        </LoadingProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
